// "Personen" (admins only): invite people (a single-use login link to hand over), edit name, short
// name and email, grant or take the admin right, disable an account, issue a new link.
import { absolute, api, ApiError, type Account } from "./account";
import { css, personColor } from "./palette";
import { shortOf } from "./presence";

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

export class People {
  private list: Account[] = [];

  constructor(private me: Account) {
    document.title = "Personen – Lanewerk";
    const name = $<HTMLInputElement>("#lw-pp-name");
    name.addEventListener("input", () => { $<HTMLInputElement>("#lw-pp-short").placeholder = shortOf(name.value) || "Kürzel"; });
    $("#lw-pp-invite").addEventListener("submit", (e) => { e.preventDefault(); void this.invite(); });
    $("#lw-pp-copy").addEventListener("click", () => void this.copy());
    void this.load();
  }

  async load() {
    try {
      this.list = await api<Account[]>("./api/users");
      this.error("");
    } catch (e) {
      this.error(`Die Personen konnten nicht geladen werden (${(e as Error).message}).`);
    }
    this.render();
  }

  private error(msg: string) { $("#lw-pp-error").textContent = msg; }

  private async invite() {
    const name = $<HTMLInputElement>("#lw-pp-name");
    const short = $<HTMLInputElement>("#lw-pp-short");
    const email = $<HTMLInputElement>("#lw-pp-email");
    const admin = $<HTMLInputElement>("#lw-pp-admin");
    if (!name.value.trim()) return name.focus();
    try {
      const r = await api<{ user: Account; link: string }>("./api/users", "POST", {
        name: name.value.trim(), short: short.value.trim(), email: email.value.trim() || null, admin: admin.checked,
      });
      name.value = short.value = email.value = "";
      admin.checked = false;
      this.showLink(r.user, r.link);
      await this.load();
    } catch (e) {
      this.error(`Einladen hat nicht geklappt (${(e as Error).message}).`);
    }
  }

  private showLink(u: Account, link: string) {
    $("#lw-pp-link-who").textContent = u.name;
    $<HTMLInputElement>("#lw-pp-link-url").value = absolute(link);
    $("#lw-pp-copy").textContent = "Kopieren";
    $("#lw-pp-link").hidden = false;
    $<HTMLInputElement>("#lw-pp-link-url").select();
  }

  private async copy() {
    const input = $<HTMLInputElement>("#lw-pp-link-url");
    try {
      await navigator.clipboard.writeText(input.value);
      $("#lw-pp-copy").textContent = "Kopiert";
    } catch {
      input.select(); // clipboard blocked (http on another host): the person copies by hand
      $("#lw-pp-copy").textContent = "Mit ⌘C kopieren";
    }
  }

  private async update(u: Account, patch: Partial<Pick<Account, "name" | "short" | "email" | "admin" | "disabled">>) {
    try {
      const n = await api<Account>(`./api/users/${encodeURIComponent(u.id)}`, "PUT", patch);
      Object.assign(u, n);
      const kept = (patch.admin === false && n.admin) || (patch.disabled === true && !n.disabled);
      this.error(kept ? "Die letzte aktive Person mit Verwaltungsrecht bleibt Admin und aktiv." : "");
    } catch (e) {
      this.error(e instanceof ApiError && e.status === 404 ? "Diese Person gibt es nicht mehr." : `Nicht gespeichert (${(e as Error).message}).`);
    }
    this.render();
  }

  private async newLink(u: Account) {
    try {
      const r = await api<{ link: string }>(`./api/users/${encodeURIComponent(u.id)}/link`, "POST");
      this.showLink(u, r.link);
    } catch (e) {
      this.error(`Kein neuer Link (${(e as Error).message}).`);
    }
  }

  private render() {
    const sorted = [...this.list].sort((a, b) => Number(a.disabled) - Number(b.disabled) || a.name.localeCompare(b.name, "de"));
    const n = this.list.filter((u) => !u.disabled).length;
    $("#lw-pp-count").textContent = `${n} ${n === 1 ? "aktive Person" : "aktive Personen"}`;
    const body = $("#lw-pp-rows");
    body.replaceChildren(...sorted.map((u) => this.row(u)));
  }

  private row(u: Account): HTMLTableRowElement {
    const tr = el("tr");
    if (u.disabled) tr.classList.add("off");
    const av = el("span", "avatar", u.short);
    av.style.background = css(personColor(u.short));
    const field = (key: "name" | "short" | "email", label: string, max: number) => {
      const i = el("input", "field");
      i.value = (u[key] as string | null) ?? "";
      i.maxLength = max;
      i.setAttribute("aria-label", `${label} von ${u.name}`);
      if (key === "email") i.type = "email";
      i.addEventListener("change", () => {
        const v = i.value.trim();
        void this.update(u, key === "email" ? { email: v || null } : { [key]: v });
      });
      return i;
    };
    const check = (key: "admin" | "disabled", label: string) => {
      const box = el("input");
      box.type = "checkbox";
      box.checked = key === "admin" ? u.admin : !u.disabled;
      box.setAttribute("aria-label", `${label}: ${u.name}`);
      box.addEventListener("change", () => void this.update(u, key === "admin" ? { admin: box.checked } : { disabled: !box.checked }));
      return box;
    };
    const link = el("button", "pp-link-btn", "Neuer Link");
    link.type = "button";
    link.title = "Neuer Anmelde-Link (neues Gerät, Link verloren)";
    link.disabled = u.disabled;
    link.addEventListener("click", () => void this.newLink(u));
    const cells: (Node | string)[][] = [
      [av],
      [field("name", "Name", 60), u.id === this.me.id ? el("span", "pp-you", "du") : ""],
      [field("short", "Kürzel", 4)],
      [field("email", "E-Mail", 120)],
      [check("admin", "Verwaltung")],
      [check("disabled", "Aktiv")],
      [link],
    ];
    for (const c of cells) {
      const td = el("td");
      td.append(...c);
      tr.appendChild(td);
    }
    return tr;
  }
}
